<template>
  <ui-select
    key="tags"
    ref="tagsSelect"
    v-model="tags"
    :search-input="searchTags"
    :class="[
      'tag-search__input',
      'tag-search__input--combobox',
    ]"
    prepend-inner-icon="fas fa-search"
    :items="tagsList"
    :multiple="true"
    item-text="name"
    chips
    :combobox="true"
    button-style="text"
    :placeholder="t('Tags')"
    :hide-details="1"
    :menuProps="{
      nudgeTop: 0,
    }"
    @blur="$emit('handleInputBlur', $event)"
    @focus="$emit('handleInputFocus', $event)"
    @change="$emit('handleTagsChange', $event)"
  >
    <template #append>
      <tag-search-append
        :has-clear="showClear"
        :has-marks="hasMarks"
        :hide-tags="hideTags"
        :selected="selected"
        @update:selected="selected = $event"
        @clear="$emit('handleClear', $event)"
      ></tag-search-append>
    </template>
  </ui-select>
</template>

<script setup lang="ts">
import { useStore } from '@/store';
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import TagSearchAppend from '@/views/reports/pickers/tag-search-append.vue';
import { populateTagsStoreCacheApiRequest } from '@/services/api/api-services/tags';
import { useI18n } from '@/composables/useI18n';

const store = useStore();

type TagEntityType =
  | 'campaigns'
  | 'offers'
  | 'flows'
  | 'landings'
  | 'traffic-sources'
  | 'affiliate-networks';

const props = defineProps<{
  hideTags: boolean;
  hasClear?: boolean;
  isShowClear?: boolean;
  hasMarks: boolean;
  searchTags: string;
  switchSearchMode: string;
}>();

const showClear = computed(() => !!(props.isShowClear || props.hasClear));

const emit = defineEmits<{
  'update:switchSearchMode': [value: string];
  handleInputBlur: [event: FocusEvent];
  handleInputFocus: [event: FocusEvent];
  handleTagsChange: [value: unknown];
  handleClear: [event: MouseEvent];
}>();

const tags = defineModel<unknown>({ default: undefined });

const { t } = useI18n();
const route = useRoute();
const entityTypesWithTags: TagEntityType[] = [
  'campaigns',
  'offers',
  'flows',
  'landings',
  'traffic-sources',
  'affiliate-networks',
];

const selected = computed({
  get: () => props.switchSearchMode,
  set: (value: string) => {
    emit('update:switchSearchMode', value);
  },
});

const tagsList = computed(() => {
  const entityType = route.params.type as TagEntityType;
  const tagsData = store.cache.tags[entityType]?.data as { name: string }[] | undefined;
  return tagsData?.map(item => item.name) || [];
});

const populateTagsIfNeeded = async (entityType: string) => {
  if (!entityTypesWithTags.includes(entityType as TagEntityType)) return;

  const tagsState = store.cache.tags[entityType as TagEntityType];
  if (!tagsState?.isUploaded) {
    await populateTagsStoreCacheApiRequest({ type: entityType });
  }
};

watch(
  () => route.params.type,
  (entityType) => {
    if (entityType) {
      populateTagsIfNeeded(entityType as string);
    }
  },
  { immediate: true },
);
</script>

<style lang="scss">

</style>
